import addCommas from '../../utils/addCommas';

export const TREND_UP = 'up';
export const TREND_DOWN = 'down';
export const TREND_STEADY = 'steady';

export const CHART_TYPE_DAYS_ON_MARKET = 'days_on_market';
export const CHART_TYPE_INVENTORY = 'inventory';
export const CHART_TYPE_NEW_LISTINGS = 'new_listings';
export const CHART_TYPE_HOME_PRICE = 'home_price';
export const CHART_TYPE_PRICE_VS_INVENTORY = 'price_vs_inventory';

const IN_LINE = 'In line with this time last year';

function formatCount(value) {
    return Number.isFinite(value) ? addCommas(Math.round(value)) : '-';
}

function formatCurrency(value) {
    return Number.isFinite(value) ? `$${addCommas(Math.round(value))}` : '-';
}

function formatDays(value) {
    return Number.isFinite(value) ? String(Math.round(value)) : '-';
}

/**
 * Copy for the four single-metric market topics. Each entry knows how to render
 * its stat line and the three year-over-year headline variants.
 */
const SINGLE_METRIC_CAPTIONS = {
    [CHART_TYPE_DAYS_ON_MARKET]: {
        format: formatDays,
        stat: (value)=> `${value} days on market`,
        [TREND_UP]: {
            headline: 'Homes are taking longer to sell',
            subline: (prior)=> `Up from ${prior} days a year ago`,
        },
        [TREND_DOWN]: {
            headline: 'Homes are selling faster than usual',
            subline: (prior)=> `Down from ${prior} days a year ago`,
        },
        [TREND_STEADY]: {
            headline: 'Homes are selling at a steady pace',
            subline: ()=> IN_LINE,
        },
    },
    [CHART_TYPE_INVENTORY]: {
        format: formatCount,
        stat: (value)=> `${value} active listings`,
        [TREND_UP]: {
            headline: 'More homes are hitting the market',
            subline: (prior)=> `Up from ${prior} this time last year`,
        },
        [TREND_DOWN]: {
            headline: 'Fewer homes are available than usual',
            subline: (prior)=> `Down from ${prior} this time last year`,
        },
        [TREND_STEADY]: {
            headline: 'Inventory is holding relatively stable',
            subline: ()=> IN_LINE,
        },
    },
    [CHART_TYPE_NEW_LISTINGS]: {
        format: formatCount,
        stat: (value)=> `${value} new listings this month`,
        [TREND_UP]: {
            headline: 'More sellers are entering the market',
            subline: (prior)=> `Up from ${prior} this time last year`,
        },
        [TREND_DOWN]: {
            headline: 'Fewer sellers are listing their homes',
            subline: (prior)=> `Down from ${prior} this time last year`,
        },
        [TREND_STEADY]: {
            headline: 'New listings holding steady',
            subline: ()=> IN_LINE,
        },
    },
    [CHART_TYPE_HOME_PRICE]: {
        format: formatCurrency,
        stat: (value)=> `${value} median listing price`,
        [TREND_UP]: {
            headline: 'Home prices are rising',
            subline: (prior)=> `Up from ${prior} this time last year`,
        },
        [TREND_DOWN]: {
            headline: 'Home prices are easing',
            subline: (prior)=> `Down from ${prior} this time last year`,
        },
        [TREND_STEADY]: {
            headline: 'Home prices are holding firm',
            subline: ()=> IN_LINE,
        },
    },
};

const plain = (text)=> ({ text });
const accent = (text)=> ({ text, accent: true });

/**
 * Price vs Inventory headlines, keyed `${priceTrend}_${inventoryTrend}`.
 * Segments marked `accent` render in the teal emphasis colour.
 */
const PRICE_VS_INVENTORY_CAPTIONS = {
    up_down: {
        headline: [
            plain('Prices are '),
            accent('higher'),
            plain(' and inventory is '),
            accent('lower'),
            plain(' than this time last year. Sellers have more '),
            accent('leverage.'),
        ],
    },
    down_up: {
        headline: [
            plain('Prices are '),
            accent('lower'),
            plain(' and inventory is '),
            accent('higher'),
            plain(' than this time last year. Buyers have more '),
            accent('leverage.'),
        ],
    },
    up_up: {
        headline: [
            plain('Inventory is '),
            accent('higher'),
            plain(' than last year, but prices are still '),
            accent('climbing.'),
        ],
    },
    down_down: {
        headline: [
            plain('Both prices and inventory are '),
            accent('lower'),
            plain(' than last year. The market is '),
            accent('finding its footing.'),
        ],
    },
    steady_steady: {
        headline: [
            plain('Prices and inventory are '),
            accent('in line'),
            plain(' with last year. The market is holding '),
            accent('steady.'),
        ],
    },
    up_steady: {
        headline: [
            plain('Prices are '),
            accent('higher'),
            plain(' than last year, while inventory is '),
            accent('holding steady.'),
        ],
    },
    down_steady: {
        headline: [
            plain('Prices are '),
            accent('lower'),
            plain(' than last year, while inventory is '),
            accent('holding steady.'),
        ],
    },
    steady_up: {
        headline: [
            plain('Prices are '),
            accent('in line'),
            plain(' with last year, while inventory is '),
            accent('higher.'),
        ],
    },
    steady_down: {
        headline: [
            plain('Prices are '),
            accent('in line'),
            plain(' with last year, while inventory is '),
            accent('lower.'),
        ],
    },
};

/**
 * Shown when exactly one of the two metrics is steady, which none of the five
 * primary variants covers.
 */
const PRICE_VS_INVENTORY_FALLBACK = {
    headline: [plain('Market conditions are '), accent('shifting.')],
    subline: 'Check the chart for the full picture.',
};

function normalizeTrend(trend) {
    return [TREND_UP, TREND_DOWN, TREND_STEADY].includes(trend) ? trend : TREND_STEADY;
}

/**
 * Resolves the copy for a single-metric template.
 *
 * @param {string} chartType - Chart type
 * @param {Object} metric - `{ current, prior, trend }`
 * @returns {{stat: string, headline: string, subline: string}|null}
 */
export function getSingleMetricCaption(chartType, metric) {
    const config = SINGLE_METRIC_CAPTIONS[chartType];

    if (!config || !metric) {
        return null;
    }

    const trend = normalizeTrend(metric.trend);
    const variant = config[trend];

    return {
        stat: config.stat(config.format(metric.current)),
        headline: variant.headline,
        subline: variant.subline(config.format(metric.prior)),
    };
}

/**
 * Resolves the Price vs Inventory headline from both trends, falling back to
 * the generic copy when exactly one metric is steady.
 *
 * @param {Object} price - `{ current, prior, trend }` for median listing price
 * @param {Object} inventory - `{ current, prior, trend }` for active listings
 * @returns {{headline: Array<{text: string, accent?: boolean}>, subline?: string}}
 */
export function getPriceVsInventoryCaption(price, inventory) {
    const priceTrend = normalizeTrend(price && price.trend);
    const inventoryTrend = normalizeTrend(inventory && inventory.trend);

    return PRICE_VS_INVENTORY_CAPTIONS[`${priceTrend}_${inventoryTrend}`] || PRICE_VS_INVENTORY_FALLBACK;
}
